import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {boolean} */
  #cartOperationInProgress = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
  }

  #handleCartAdd = () => {
    this.#cartOperationInProgress = true;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleCartUpdate = () => {
    this.#cartOperationInProgress = false;
  };

  /**
   * Refreshes the cart content by re-rendering the cart items component section
   */
  #refreshCartContent = () => {
    const cartItemsComponent = /** @type {HTMLElement | null} */ (this.querySelector('cart-items-component'));
    if (cartItemsComponent && cartItemsComponent.dataset.sectionId) {
      // Force refresh without cache to get the latest cart data
      sectionRenderer.renderSection(cartItemsComponent.dataset.sectionId, { cache: false });
    }
  };

  open() {
    this.showDialog();
    
    // Only refresh if no cart operation is in progress
    // This prevents interference with ongoing cart updates
    if (!this.#cartOperationInProgress) {
      setTimeout(() => {
        this.#refreshCartContent();
      }, 50);
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
